import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { CircularProgress, Typography } from '@occc/ui-design-system';
import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useChainId } from '@occc-app/Chain/hooks';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { FileDownloadController } from '@occc/feat-file-download';
import { useGetAudienceReportFileDownloadLink } from '@occc-app/dataAccessLayer/audience/useGetAudienceReportFileDownloadLink';
import { createAudienceExportReportFileName } from '@occc-app/routes/Audience/AudienceExportReportPage/audience-export-report.utils';
import { useGetAudienceWithDataById } from '../../../dataAccessLayer/audience/useGetAudienceWithDataById';
import { getAudienceExportPageHeaderLinks } from '../AudienceDetailsPage/utils';
import { AUDIENCE_EXPORT_DEFAULT_NAME, messages } from '../components/messages';

export const AudienceExportReportPageContainer = () => {
  const chainId = useChainId();
  const { id: audienceId = '', reportId = '' } = useParams<{ id: string; reportId: string }>();
  const { data: audience, loading: isAudienceLoading } = useGetAudienceWithDataById(
    audienceId,
    chainId,
  );
  const {
    s3Url,
    loading: isReportLinkLoading,
    error,
  } = useGetAudienceReportFileDownloadLink(reportId);

  const links = useMemo(
    () =>
      getAudienceExportPageHeaderLinks({
        audienceName: audience?.name || 'Loading...',
        audienceId: audienceId,
      }),
    [audience?.name, audienceId],
  );

  useAppLayoutContextConsumer(links);

  if (isReportLinkLoading || isAudienceLoading) {
    return <CircularProgress size={24} />;
  }

  if (error || !s3Url) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  return (
    <FileDownloadController
      loader={{
        type: 's3',
        s3Url: s3Url,
      }}
      filename={createAudienceExportReportFileName({
        audienceName: audience.name || AUDIENCE_EXPORT_DEFAULT_NAME,
      })}
      pageTitle={messages.downloadPageTitle}
      title={messages.downloadTitle}
      subtitle={messages.downloadSubtitle}
    />
  );
};
